import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { user } from 'ampersand-app' // FLAG:useDocuAi — gates the PlaceHolder fallback props (F16.1)
import { makeStyles } from '@mui/styles'

import FileService from '@/modules/file/fileService'
import { experimentalFeatures } from '@/modules/base/util/constants' // FLAG:useDocuAi
import PlaceHolder from '@/modules/file/components/placeholder'
import lang from '@/lang'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import ThemeInterface from '@/theme-interface'
import { useAppSelector } from '@/redux/store'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
  },
  img: {
    transition: 'transform 0.1s ease-in-out',
    top: '50%',
    left: '50%',
    transform: (props: { scale: number }) => `translate(-50%, -50%) scale(${props?.scale || 1})`,
    position: 'absolute',
    maxHeight: '100%',
    transformOrigin: 'center top',
    marginTop: 45,
    paddingBottom: 40,
  },
  modal: {
    width: 500,
    padding: 40,
  },
  select: {
    margin: '10px 0px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  image: {
    display: 'flex',
    justifyContent: 'center',
    objectFit: 'contain',
  },
  thumbnailPreview: {
    height: 'calc(100vh - 88px)',
    overflow: 'hidden',
    position: 'relative',
    '& > *': {
      height: '100%',
    }
  },
  previewBanner: {
    position: 'absolute',
    background: CssHelper.alpha(theme.grey1, 0.5),
    transform: 'rotateZ(-45deg) translateY(-19px) translateX(-63px)',
    padding: '10px 70px',
    fontSize: 20,
    color: 'white',
    top: 0,
    height: 50,
  },
}))

const rotateAndScaleImage = (url, rotate, callback) => {
  if (!url) return

  const img = new Image()
  img.src = url
  img.crossOrigin = 'Anonymous'
  img.onload = function() {
    const canvas = document.createElement('canvas')
    canvas.style.position = 'absolute'
    const ctx = canvas.getContext('2d')
    ctx.save()
    const width = (rotate === 0 || rotate === 180) ? img.width : img.height
    const height = (rotate === 0 || rotate === 180) ? img.height : img.width
    canvas.width = width
    canvas.height = height

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.translate(width / 2, height / 2)
    ctx.rotate(rotate * Math.PI / 180)
    ctx.drawImage(img, -img.width / 2, -img.height / 2)
    ctx.restore()
    callback(canvas.toDataURL())
  }
}

export const DocumentDrawerPreviewImage = (props) => {
  const {
    customClasses,
    document,
    showThumbnail,
    rotate,
  } = props
  const styles = useStyles(props)
  const classes = { ...styles, ...customClasses }
  const [url, setUrl] = useState(null)
  const [imgSrc, setImgSrc] = useState(null)
  const imgRef = useRef()
  const dmsFileUrl = useAppSelector((state) => state.tenant.settings.dmsFileUrl)

  useEffect(() => {
    setUrl(null)

    if (dmsFileUrl && document?.minioId && !showThumbnail) {
      FileService.streamDmsFile(document.minioId).then(setUrl)
    } else {
      FileService.fetchFileDataByUrl(document?.url).then(setUrl)
    }
  }, [document?.id])

  useEffect(() => rotateAndScaleImage(url, rotate, setImgSrc), [url, rotate])

  if (showThumbnail) {
    // FLAG:useDocuAi — gates the fallback props below (F16.1): flag-OFF keeps the baseline
    // PlaceHolder call (minioId + size only), so office thumbnails render the generic icon
    // exactly as before the feature.
    const useDocuAiEnabled = !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
    return (
      <div className={classes.thumbnailPreview}>
        {/* mimetype + name are what PlaceHolder falls back to while the DMS thumbnail is not
            ready yet, or when the file has none at all. Without them every such file showed
            the generic "unknown file" icon instead of its own (zip / doc / xml / …) — which
            is the whole reason this branch now also serves archives and other viewer-less
            types, not just the office ones. Flag-ON only (F16.1). */}
        <PlaceHolder
          minioId={document.minioId}
          size={1000}
          {...(useDocuAiEnabled
            ? { mimetype: document.mimetype, name: document.decodedName ?? document.name }
            : {})}
        />
        <div className={classes.previewBanner}>{lang.generics.preview}</div>
      </div>
    )
  }

  const content = imgSrc
    ? <img src={imgSrc} ref={imgRef} className={classes.img} />
    : (
      <PlaceHolder
        mimetype={document?.mimetype}
        thumbnailUrl={document?.thumbnailUrl}
        objectUrl={null}
      />
    )

  return (
    <>
      { content }
    </>
  )
}

DocumentDrawerPreviewImage.props = {
  document: PropTypes.object.isRequired,
}

export default DocumentDrawerPreviewImage
